import type { WorkflowAction, WorkflowCommand, WorkflowCommandResult, WorkflowSideEffectType } from "@swot/swot-components";
import type { CmtsCommandContext } from "../view-models/complaintViewModels.js";
import { createCorrelationId } from "../api/cmtsHttpClient.js";
import { readArray, readString, readBoolean } from "./mapperUtils.js";

const KNOWN_SIDE_EFFECTS: readonly WorkflowSideEffectType[] = ["audit", "traceability", "task", "notification", "calendar", "dashboardSignal", "evidence", "slaRecalculation"];

export function mapWorkflowAction(dto: unknown): WorkflowAction {
  const sideEffects = readArray(dto, ["createsSideEffects", "requiredSideEffects", "sideEffects"], (x) => readString(x, ["type", "code"], String(x)))
    .filter((x): x is WorkflowSideEffectType => (KNOWN_SIDE_EFFECTS as readonly string[]).includes(x));
  return {
    code: readString(dto, ["code", "actionCode"], "UNKNOWN_ACTION"),
    label: readString(dto, ["label", "actionLabel", "displayName"], readString(dto, ["code", "actionCode"], "Action")),
    fromStatuses: readArray(dto, ["fromStatuses", "allowedFromStatuses"], (x) => String(x)),
    toStatus: readString(dto, ["toStatus", "targetStatus"], "UNKNOWN"),
    requiredPermission: readString(dto, ["requiredPermission", "permissionCode"], "CMTS.ACTION.EXECUTE"),
    requiresReason: readBoolean(dto, ["requiresReason", "reasonRequired"], false),
    requiresEvidence: readBoolean(dto, ["requiresEvidence", "evidenceRequired"], false),
    requiresComment: readBoolean(dto, ["requiresComment", "commentRequired"], false),
    requiredFields: readArray(dto, ["requiredFields"], (x) => String(x)),
    sodConflictRoleCodes: readArray(dto, ["sodConflictRoleCodes", "conflictRoles"], (x) => String(x)),
    createsSideEffects: sideEffects,
    description: readString(dto, ["description"], ""),
  };
}

export function buildWorkflowCommand(ctx: CmtsCommandContext): WorkflowCommand {
  return {
    commandId: createCorrelationId(),
    recordId: ctx.complaintId,
    recordType: ctx.recordType,
    actionCode: ctx.actionCode,
    fromStatus: ctx.currentStatus,
    actorId: ctx.actorId,
    actorName: ctx.actorName,
    payload: {
      comment: ctx.comment,
      reasonCode: ctx.reasonCode,
      evidenceIds: ctx.evidenceIds,
      assignedTo: ctx.assignedTo,
      dueDate: ctx.dueDate,
      metadata: ctx.metadata,
    },
    requiredSideEffects: ["audit", "traceability", "notification", "task", "dashboardSignal", "slaRecalculation"],
    issuedAt: new Date().toISOString(),
  };
}

export function mapWorkflowResult(dto: unknown, command: WorkflowCommand): WorkflowCommandResult {
  return {
    ok: readBoolean(dto, ["ok", "success"], true),
    command,
    newStatus: readString(dto, ["newStatus", "status", "targetStatus"], ""),
    diagnostics: [],
    sideEffectsCreated: readArray(dto, ["sideEffectsCreated", "sideEffects"], (x) => ({
      type: readString(x, ["type"], "audit") as WorkflowSideEffectType,
      id: readString(x, ["id", "eventId"], createCorrelationId()),
      status: readString(x, ["status"], "created") as "created" | "skipped" | "failed",
      message: readString(x, ["message"], ""),
    })),
  };
}
